import React, { useState, useEffect, useCallback } from 'react';
import './CreateGroupModal.css';

const CreateGroupModal = ({ onClose, onCreated }) => {
  const [step, setStep] = useState(1);
  const [groupName, setGroupName] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const searchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const url = `/api/profile/search?q=${encodeURIComponent(searchQuery)}`;
      console.log('Searching users with URL:', url); // Debug log
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      console.log('Search response status:', response.status); // Debug log
      
      if (response.ok) {
        const data = await response.json();
        console.log('Search results received:', data); // Debug log
        console.log('Number of users:', data.users?.length); // Debug log
        setSearchResults(data.users || []);
        console.log('Search results state updated'); // Debug log
      } else {
        const errorData = await response.text();
        console.error('Search failed:', response.status, errorData);
        setSearchResults([]);
        setError(`Search failed: ${response.status}`);
      }
    } catch (err) {
      console.error('Error searching users:', err);
      setSearchResults([]);
      setError(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    // Debounce search
    const timer = setTimeout(() => {
      if (searchQuery.trim().length >= 2) {
        searchUsers();
      } else {
        setSearchResults([]);
        setLoading(false);
      }
    }, 300); // 300ms debounce

    // Show loading immediately when typing
    if (searchQuery.trim().length >= 2) {
      setLoading(true);
    }

    return () => clearTimeout(timer);
  }, [searchQuery, searchUsers]);

  const toggleUserSelection = (user) => {
    setSelectedUsers(prev => {
      const exists = prev.find(u => u._id === user._id);
      if (exists) {
        return prev.filter(u => u._id !== user._id);
      } else {
        return [...prev, user];
      }
    });
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim()) {
      setError('Group name is required');
      return;
    }

    if (selectedUsers.length < 1) {
      setError('Select at least 1 other member');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/messages/groups', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          participantIds: selectedUsers.map(u => u._id),
          groupName: groupName.trim(),
          groupDescription: groupDescription.trim()
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Group created:', data); // Debug log
        if (onCreated) {
          onCreated(data.data);
        }
        onClose();
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to create group');
      }
    } catch (err) {
      console.error('Error creating group:', err);
      setError('Failed to create group');
    } finally {
      setLoading(false);
    }
  };

  // Debug log to check state
  console.log('CreateGroupModal render - searchQuery:', searchQuery, 'searchResults:', searchResults, 'loading:', loading);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="create-group-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Create Group Chat</h2>
          <button onClick={onClose} className="modal-close-btn">✕</button>
        </div>

        {error && (
          <div className="modal-error">
            {error}
          </div>
        )}

        <div className="modal-body">
          {step === 1 ? (
            <div className="group-info-step">
              <div className="form-group">
                <label>Group Name *</label>
                <input
                  type="text"
                  value={groupName}
                  onChange={e => setGroupName(e.target.value)}
                  placeholder="Enter group name..."
                  maxLength={100}
                  autoFocus
                />
                <div className="char-count">{groupName.length}/100</div>
              </div>

              <div className="form-group">
                <label>Description (Optional)</label>
                <textarea
                  value={groupDescription}
                  onChange={e => setGroupDescription(e.target.value)}
                  placeholder="What's this group about?"
                  maxLength={500}
                  rows={3}
                />
                <div className="char-count">{groupDescription.length}/500</div>
              </div>

              <button
                onClick={() => setStep(2)}
                className="next-btn"
                disabled={!groupName.trim()}
              >
                Next: Add Members →
              </button>
            </div>
          ) : (
            <div className="add-members-step">
              <div className="selected-users">
                {selectedUsers.length > 0 ? (
                  <div className="selected-users-list">
                    {selectedUsers.map(user => (
                      <div key={user._id} className="selected-user-chip">
                        <span>{user.username}</span>
                        <button onClick={() => toggleUserSelection(user)}>✕</button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-selection">No members selected yet</div>
                )}
              </div>
              
              <div className="search-instructions">
                <p>🔍 Search for users by username to add them to the group</p>
                <p className="search-tip">Click on a user to select/deselect them</p>
              </div>

              <div className="user-search">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => {
                    setSearchQuery(e.target.value);
                    setError(null); // Clear error when typing
                  }}
                  placeholder="Search users..."
                  className="search-input"
                />
                
                {error && (
                  <div className="search-error">⚠️ {error}</div>
                )}
                
                {searchQuery.length > 0 && (
                  <div className="search-results">
                    {loading ? (
                      <div className="search-loading">🔍 Searching...</div>
                    ) : error ? (
                      <div className="search-error-box">❌ {error}</div>
                    ) : searchResults.length > 0 ? (
                      <>
                        <div className="search-results-header">
                          ✅ Found {searchResults.length} user{searchResults.length !== 1 ? 's' : ''} - Click to add:
                        </div>
                        {searchResults.map(user => (
                          <div
                            key={user._id}
                            className={`user-result ${selectedUsers.find(u => u._id === user._id) ? 'selected' : ''}`}
                            onClick={() => toggleUserSelection(user)}
                          >
                            <div className="user-avatar">
                              {user.profile?.avatar?.url ? (
                                <img src={user.profile.avatar.url} alt={user.username} />
                              ) : (
                                <div className="avatar-placeholder">{user.username[0].toUpperCase()}</div>
                              )}
                            </div>
                            <div className="user-info">
                              <div className="user-name">{user.username}</div>
                              {user.accountType && (
                                <div className="user-type">{user.accountType}</div>
                              )}
                            </div>
                            {selectedUsers.find(u => u._id === user._id) && (
                              <div className="user-selected-icon">✓</div>
                            )}
                          </div>
                        ))}
                      </>
                    ) : searchQuery.length >= 2 ? (
                      <div className="search-empty">❌ No users found</div>
                    ) : (
                      <div className="search-hint">💡 Type at least 2 characters to search</div>
                    )}
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button onClick={() => setStep(1)} className="back-btn">
                  ← Back
                </button>
                <button
                  onClick={handleCreateGroup}
                  className="create-btn"
                  disabled={loading || selectedUsers.length < 1}
                >
                  {loading ? 'Creating...' : `Create Group (${selectedUsers.length + 1} members)`}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateGroupModal;
